import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle2, AlertCircle, DollarSign, Edit2, FileSpreadsheet, Table, ArrowRight, Columns, AlertTriangle, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UploadInvoices() {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null); // 'pdf' or 'spreadsheet'
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const fileInputRef = useRef(null);
  
  // Column mapping state
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    agency_name: '',
    invoice_number: '',
    invoice_date: '',
    amount: '',
    notes: ''
  });
  
  // Duplicate detection state
  const [showDuplicateCheck, setShowDuplicateCheck] = useState(false);
  const [duplicateCheckResult, setDuplicateCheckResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    processFile(droppedFile);
  };

  const processFile = (selectedFile) => {
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (selectedFile.type === 'application/pdf' || fileName.endsWith('.pdf')) {
        setFile(selectedFile);
        setFileType('pdf');
        setUploadResult(null);
        setEditMode(false);
        setEditedData(null);
      } else if (
        fileName.endsWith('.csv') || 
        fileName.endsWith('.xlsx') || 
        fileName.endsWith('.xls') ||
        selectedFile.type === 'text/csv' ||
        selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        selectedFile.type === 'application/vnd.ms-excel'
      ) {
        setFile(selectedFile);
        setFileType('spreadsheet');
        setUploadResult(null);
        setEditMode(false);
        setEditedData(null);
      } else {
        toast.error('Please select a PDF, CSV, or Excel file');
      }
    }
  };
  
  // Check for duplicates after column mapping
  const handleCheckDuplicates = async () => {
    if (!file || !isColumnMappingComplete()) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const params = new URLSearchParams({
        agency_col: columnMapping.agency_name,
        invoice_num_col: columnMapping.invoice_number,
        date_col: columnMapping.invoice_date,
        amount_col: columnMapping.amount
      });
      
      const response = await axios.post(`${API}/invoices/check-duplicates?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setDuplicateCheckResult(response.data);
      setShowDuplicateCheck(true);
      setShowColumnMapping(false);
      
      if (response.data.duplicate_count > 0) {
        toast.warning(`Found ${response.data.duplicate_count} potential duplicate(s)`);
      } else {
        toast.success('No duplicates found');
      }
    } catch (error) {
      console.error('Duplicate check error:', error);
      toast.error(error.response?.data?.detail || 'Failed to check duplicates');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    // For spreadsheets, first preview columns for mapping
    if (fileType === 'spreadsheet' && !showColumnMapping && !showDuplicateCheck) {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${API}/invoices/preview-spreadsheet`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setPreviewData(response.data);
        setColumnMapping(response.data.suggested_mapping || {});
        setShowColumnMapping(true);
        toast.info(`Found ${response.data.row_count} rows. Please map your columns.`);
      } catch (error) {
        console.error('Preview error:', error);
        const message = error.response?.data?.detail || 'Failed to preview file';
        toast.error(message);
      } finally {
        setUploading(false);
      }
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      let response;
      
      if (fileType === 'spreadsheet' && (showColumnMapping || showDuplicateCheck)) {
        // Use mapped upload endpoint with skip_duplicates option
        const params = new URLSearchParams({
          agency_col: columnMapping.agency_name,
          invoice_num_col: columnMapping.invoice_number,
          date_col: columnMapping.invoice_date,
          amount_col: columnMapping.amount,
          skip_duplicates: skipDuplicates.toString()
        });
        if (columnMapping.notes) {
          params.append('notes_col', columnMapping.notes);
        }
        
        response = await axios.post(`${API}/invoices/upload-spreadsheet-mapped?${params.toString()}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const endpoint = fileType === 'spreadsheet' 
          ? `${API}/invoices/upload-spreadsheet`
          : `${API}/invoices/upload`;
          
        response = await axios.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setUploadResult(response.data);
      setShowColumnMapping(false);
      setShowDuplicateCheck(false);
      setDuplicateCheckResult(null);
      setPreviewData(null);
      
      if (response.data.invoices && response.data.invoices.length > 0) {
        const count = response.data.invoices_created || response.data.invoices.length;
        if (count > 1) {
          toast.success(`${count} invoices uploaded successfully!`);
        } else {
          toast.success('Invoice uploaded and processed successfully');
          // For single invoice, set up editing
          const inv = response.data.invoices[0];
          setEditedData({
            invoice_id: inv.invoice_id,
            agency_name: inv.agency_name || '',
            invoice_number: inv.invoice_number || '',
            invoice_date: inv.invoice_date || new Date().toISOString().split('T')[0],
            amount: inv.amount || 0
          });
        }
      } else if (response.data.warning) {
        toast.warning(response.data.warning);
        setEditMode(true);
        setEditedData({
          agency_name: '',
          invoice_number: '',
          invoice_date: new Date().toISOString().split('T')[0],
          amount: 0
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateInvoice = async () => {
    if (!uploadResult?.invoice_id || !editedData) return;

    try {
      await axios.put(`${API}/invoices/${uploadResult.invoice_id}`, null, {
        params: {
          agency_name: editedData.agency_name,
          invoice_number: editedData.invoice_number,
          invoice_date: editedData.invoice_date,
          amount: editedData.amount
        }
      });
      toast.success('Invoice updated successfully');
      setEditMode(false);
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update invoice');
    }
  };

  const resetForm = () => {
    setFile(null);
    setFileType(null);
    setUploadResult(null);
    setEditMode(false);
    setEditedData(null);
    setShowColumnMapping(false);
    setShowDuplicateCheck(false);
    setDuplicateCheckResult(null);
    setPreviewData(null);
    setSkipDuplicates(true);
    setColumnMapping({
      agency_name: '',
      invoice_number: '',
      invoice_date: '',
      amount: '',
      notes: ''
    });
  };
  
  const isColumnMappingComplete = () => {
    return columnMapping.agency_name && 
           columnMapping.invoice_number && 
           columnMapping.invoice_date && 
           columnMapping.amount;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Upload Invoices</h1>
          <p className="text-muted-foreground mt-1">
            Upload PDF invoices for automatic extraction and verification tracking
          </p>
        </div>
        <Button variant="outline" onClick={() => window.location.href = '/admin/invoices'}>
          View All Invoices
        </Button>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <AlertCircle className="text-blue-600 mt-0.5" size={18} />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Multiple Upload Options</p>
              <p className="mt-1">
                <strong>PDF:</strong> AI extracts invoice details automatically.<br/>
                <strong>CSV/Excel:</strong> Import multiple invoices at once. Required columns: agency_name, invoice_number, invoice_date, amount
              </p>
            </div>
          </div>

          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-3">
              {file ? (
                <>
                  {fileType === 'spreadsheet' ? (
                    <FileSpreadsheet className="w-12 h-12 text-green-600" />
                  ) : (
                    <FileText className="w-12 h-12 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium text-foreground">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB • {fileType === 'spreadsheet' ? 'Spreadsheet Import' : 'PDF (AI Extraction)'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <FileText className="w-10 h-10 text-red-400" />
                    <FileSpreadsheet className="w-10 h-10 text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      Drop your invoice file here or click to browse
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Supports PDF, CSV, and Excel files (.xlsx, .xls)
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {!uploadResult && !showColumnMapping && (
            <div className="flex gap-3">
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="flex-1"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    {fileType === 'spreadsheet' ? 'Reading Columns...' : 'Processing Invoice...'}
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    {fileType === 'spreadsheet' ? 'Preview & Map Columns' : 'Upload & Extract'}
                  </>
                )}
              </Button>
              {file && (
                <Button variant="outline" onClick={resetForm}>
                  Clear
                </Button>
              )}
            </div>
          )}
          
          {/* Column Mapping UI for Spreadsheets */}
          {showColumnMapping && previewData && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Columns className="text-blue-600" size={18} />
                  <p className="font-medium text-blue-800">Map Your Columns</p>
                </div>
                <p className="text-sm text-blue-700">
                  Found {previewData.row_count} rows in "{previewData.filename}". 
                  Please map your spreadsheet columns to the required fields below.
                </p>
              </div>
              
              {/* Column Mapping Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Agency Name <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={columnMapping.agency_name} 
                    onValueChange={(v) => setColumnMapping({...columnMapping, agency_name: v})}
                  >
                    <SelectTrigger data-testid="map-agency-name">
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Invoice Number <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={columnMapping.invoice_number} 
                    onValueChange={(v) => setColumnMapping({...columnMapping, invoice_number: v})}
                  >
                    <SelectTrigger data-testid="map-invoice-number">
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Invoice Date <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={columnMapping.invoice_date} 
                    onValueChange={(v) => setColumnMapping({...columnMapping, invoice_date: v})}
                  >
                    <SelectTrigger data-testid="map-invoice-date">
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Amount <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={columnMapping.amount} 
                    onValueChange={(v) => setColumnMapping({...columnMapping, amount: v})}
                  >
                    <SelectTrigger data-testid="map-amount">
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label>Notes (Optional)</Label>
                  <Select 
                    value={columnMapping.notes || ''} 
                    onValueChange={(v) => setColumnMapping({...columnMapping, notes: v})}
                  >
                    <SelectTrigger data-testid="map-notes">
                      <SelectValue placeholder="Select column (optional)..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">-- None --</SelectItem>
                      {previewData.columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Preview Table */}
              {previewData.sample_data?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-4 py-2 font-medium text-sm">
                    Sample Data Preview (first {previewData.sample_data.length} rows)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          {previewData.columns.map(col => (
                            <th key={col} className="text-left p-2 font-medium whitespace-nowrap">
                              {col}
                              {columnMapping.agency_name === col && <span className="ml-1 text-xs text-blue-600">(Agency)</span>}
                              {columnMapping.invoice_number === col && <span className="ml-1 text-xs text-blue-600">(Invoice #)</span>}
                              {columnMapping.invoice_date === col && <span className="ml-1 text-xs text-blue-600">(Date)</span>}
                              {columnMapping.amount === col && <span className="ml-1 text-xs text-blue-600">(Amount)</span>}
                              {columnMapping.notes === col && <span className="ml-1 text-xs text-blue-600">(Notes)</span>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.sample_data.map((row, idx) => (
                          <tr key={idx} className="border-t">
                            {previewData.columns.map(col => (
                              <td key={col} className="p-2 whitespace-nowrap">
                                {row[col] || <span className="text-muted-foreground">-</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleCheckDuplicates}
                  disabled={!isColumnMappingComplete() || uploading}
                  className="flex-1"
                  data-testid="check-duplicates-btn"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Checking for Duplicates...
                    </>
                  ) : (
                    <>
                      <Copy size={16} className="mr-2" />
                      Check for Duplicates
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          {/* Duplicate Check Results */}
          {showDuplicateCheck && duplicateCheckResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className={`p-4 rounded-lg ${
                duplicateCheckResult.duplicate_count > 0 
                  ? 'bg-amber-50 border border-amber-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {duplicateCheckResult.duplicate_count > 0 ? (
                    <AlertTriangle className="text-amber-600" size={20} />
                  ) : (
                    <CheckCircle2 className="text-green-600" size={20} />
                  )}
                  <p className="font-medium">
                    {duplicateCheckResult.duplicate_count > 0 
                      ? `Found ${duplicateCheckResult.duplicate_count} potential duplicate(s)` 
                      : 'No duplicates found'}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {duplicateCheckResult.new_count} new invoice(s) ready to import
                </p>
              </div>
              
              {/* Duplicate List */}
              {duplicateCheckResult.duplicate_count > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="skip-duplicates" 
                      checked={skipDuplicates}
                      onCheckedChange={setSkipDuplicates}
                    />
                    <Label htmlFor="skip-duplicates" className="cursor-pointer">
                      Skip duplicates (import only new invoices)
                    </Label>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-amber-50 px-4 py-2 font-medium text-sm flex items-center gap-2">
                      <Copy size={16} className="text-amber-600" />
                      Potential Duplicates ({duplicateCheckResult.duplicate_count})
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/30 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Row</th>
                            <th className="text-left p-2 font-medium">Invoice #</th>
                            <th className="text-left p-2 font-medium">Agency (File)</th>
                            <th className="text-left p-2 font-medium">Existing Agency</th>
                            <th className="text-right p-2 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {duplicateCheckResult.duplicates.map((dup, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2">{dup.row_index}</td>
                              <td className="p-2 font-mono text-xs">{dup.invoice_number}</td>
                              <td className="p-2">{dup.agency_name}</td>
                              <td className="p-2 text-muted-foreground">
                                {dup.existing_invoice?.agency_name}
                                {dup.existing_invoice?.verified && (
                                  <span className="ml-1 text-xs text-green-600">(verified)</span>
                                )}
                              </td>
                              <td className="p-2 text-right">
                                ${parseFloat(dup.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              
              {/* New Invoices Preview */}
              {duplicateCheckResult.new_count > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-green-50 px-4 py-2 font-medium text-sm flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-green-600" />
                    New Invoices ({duplicateCheckResult.new_count})
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Agency</th>
                          <th className="text-left p-2 font-medium">Invoice #</th>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duplicateCheckResult.new_invoices.slice(0, 10).map((inv, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{inv.agency_name}</td>
                            <td className="p-2 font-mono text-xs">{inv.invoice_number}</td>
                            <td className="p-2">{inv.invoice_date}</td>
                            <td className="p-2 text-right font-medium">
                              ${parseFloat(inv.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                        {duplicateCheckResult.new_count > 10 && (
                          <tr className="border-t bg-muted/20">
                            <td colSpan={4} className="p-2 text-center text-muted-foreground">
                              ... and {duplicateCheckResult.new_count - 10} more
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleUpload}
                  disabled={uploading || (skipDuplicates && duplicateCheckResult.new_count === 0)}
                  className="flex-1"
                  data-testid="import-btn"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <ArrowRight size={16} className="mr-2" />
                      {skipDuplicates 
                        ? `Import ${duplicateCheckResult.new_count} New Invoice(s)`
                        : `Import All ${duplicateCheckResult.total_rows} Invoice(s)`
                      }
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => { setShowDuplicateCheck(false); setShowColumnMapping(true); }}>
                  Back to Mapping
                </Button>
                <Button variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Extraction Results */}
          {uploadResult && (
            <div className="space-y-4">
              {/* Success banner */}
              <div className={`p-4 rounded-lg ${
                uploadResult.warning ? 'bg-yellow-50 border border-yellow-200' : 
                uploadResult.errors?.length > 0 ? 'bg-amber-50 border border-amber-200' :
                'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center gap-2">
                  {uploadResult.warning ? (
                    <AlertCircle className="text-yellow-600" size={20} />
                  ) : uploadResult.errors?.length > 0 ? (
                    <AlertCircle className="text-amber-600" size={20} />
                  ) : (
                    <CheckCircle2 className="text-green-600" size={20} />
                  )}
                  <p className="font-medium">
                    {uploadResult.warning || 
                     (uploadResult.invoices_created > 1 
                       ? `${uploadResult.invoices_created} invoices imported successfully!`
                       : 'Invoice processed successfully!')}
                  </p>
                </div>
                {uploadResult.errors?.length > 0 && (
                  <p className="text-sm text-amber-700 mt-1">
                    {uploadResult.errors.length} row(s) had errors and were skipped
                  </p>
                )}
              </div>

              {/* Spreadsheet import results - show table of imported invoices */}
              {fileType === 'spreadsheet' && uploadResult.invoices?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-4 py-2 font-medium text-sm flex items-center gap-2">
                    <Table size={16} />
                    Imported Invoices ({uploadResult.invoices.length})
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Agency</th>
                          <th className="text-left p-2 font-medium">Invoice #</th>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.invoices.map((inv, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{inv.agency_name}</td>
                            <td className="p-2">{inv.invoice_number}</td>
                            <td className="p-2">{inv.invoice_date}</td>
                            <td className="p-2 text-right font-medium">
                              ${parseFloat(inv.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Single PDF invoice edit form */}
              {editedData && fileType === 'pdf' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Agency Name</Label>
                      <Input
                        value={editedData.agency_name || ''}
                        onChange={(e) => setEditedData({ ...editedData, agency_name: e.target.value })}
                        disabled={!editMode}
                        data-testid="agency-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Invoice Number</Label>
                      <Input
                        value={editedData.invoice_number || ''}
                        onChange={(e) => setEditedData({ ...editedData, invoice_number: e.target.value })}
                        disabled={!editMode}
                        data-testid="invoice-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Invoice Date</Label>
                      <Input
                        type="date"
                        value={editedData.invoice_date || ''}
                        onChange={(e) => setEditedData({ ...editedData, invoice_date: e.target.value })}
                        disabled={!editMode}
                        data-testid="invoice-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                        <Input
                          type="number"
                          step="0.01"
                          value={editedData.amount || 0}
                          onChange={(e) => setEditedData({ ...editedData, amount: parseFloat(e.target.value) })}
                          disabled={!editMode}
                          className="pl-8"
                          data-testid="amount-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    {editMode ? (
                      <>
                        <Button onClick={handleUpdateInvoice} className="flex-1">
                          <CheckCircle2 size={16} className="mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={() => setEditMode(false)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" onClick={() => setEditMode(true)}>
                          <Edit2 size={16} className="mr-2" />
                          Edit Details
                        </Button>
                        <Button onClick={resetForm} className="flex-1">
                          Upload Another Invoice
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Spreadsheet upload - just show upload another button */}
              {fileType === 'spreadsheet' && (
                <Button onClick={resetForm} className="w-full">
                  <Upload size={16} className="mr-2" />
                  Upload Another File
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">How Invoice Verification Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
              1
            </div>
            <div>
              <p className="font-medium">Upload PDF</p>
              <p className="text-sm text-muted-foreground">
                Upload the invoice PDF. AI extracts key details automatically.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
              2
            </div>
            <div>
              <p className="font-medium">Agency Verifies</p>
              <p className="text-sm text-muted-foreground">
                Agencies can view their invoices and mark them as verified.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
              3
            </div>
            <div>
              <p className="font-medium">Weekly Reports</p>
              <p className="text-sm text-muted-foreground">
                Receive weekly emails listing unverified invoices.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
